import React, { useState } from "react";
import { FileJson, Download, Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button, type ButtonProps } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { fetchVCon, downloadJson, vconFileName } from "@/util/vconExport";
import type { VConResource } from "@/util/vconExport";

interface VConExportButtonProps {
  resource: VConResource;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  label?: string;
  iconOnly?: boolean;
}

const VConExportButton: React.FC<VConExportButtonProps> = ({
  resource,
  variant = "outline",
  size = "sm",
  label = "Export vCon",
  iconOnly = false,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vcon, setVCon] = useState<unknown>(null);
  const [copied, setCopied] = useState(false);

  const loadVCon = async () => {
    setLoading(true);
    try {
      setVCon(await fetchVCon(resource));
    } catch {
      toast.error("Failed to load vCon");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (event: React.MouseEvent) => {
    event.stopPropagation();
    setVCon(null);
    setCopied(false);
    setOpen(true);
    void loadVCon();
  };

  const handleDownload = () => {
    if (vcon) {
      downloadJson(vconFileName(resource), vcon);
    }
  };

  const handleCopy = async () => {
    if (!vcon) {
      return;
    }
    await navigator.clipboard.writeText(JSON.stringify(vcon, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      <Button
        variant={variant}
        size={iconOnly ? "icon" : size}
        className={iconOnly ? "h-8 w-8" : undefined}
        onClick={handleOpen}
        title="Export vCon"
      >
        <FileJson className={iconOnly ? "h-4 w-4" : "h-3.5 w-3.5 mr-1"} />
        {!iconOnly && label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>vCon export</DialogTitle>
            <DialogDescription>
              Standardized IETF vCon conversation record. Download or copy it to share with a CRM, analytics, or
              compliance archive.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <pre className="max-h-[50vh] overflow-auto rounded-md bg-muted p-3 text-xs leading-relaxed">
              {vcon ? JSON.stringify(vcon, null, 2) : "No data"}
            </pre>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={handleCopy} disabled={!vcon}>
              {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button onClick={handleDownload} disabled={!vcon}>
              <Download className="h-4 w-4 mr-1" />
              Download .json
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VConExportButton;
